import { Link } from '@tanstack/react-router';
import type { ErrorComponentProps } from '@tanstack/react-router';
import { Trans } from '@lingui/react/macro';
import { APIError } from '../services/apiClient';
import ThemeLogo from './ui/ThemeLogo';

type ErrorVariant = 'maintenance' | 'rateLimit' | 'server';

// Map a thrown error to a copy variant. Anything that isn't a recognized
// backend status falls back to the generic server-error treatment.
function getErrorVariant(error: unknown): ErrorVariant {
  if (error instanceof APIError) {
    if (error.status === 503) return 'maintenance';
    if (error.status === 400 || error.status === 429) return 'rateLimit';
  }
  return 'server';
}

function ErrorCopy({ variant }: { variant: ErrorVariant }) {
  switch (variant) {
    case 'maintenance':
      return (
        <>
          <h1 className="text-copy-primary text-2xl font-semibold">
            <Trans>We'll be right back.</Trans>
          </h1>
          <p className="text-copy-secondary max-w-sm text-sm text-balance">
            <Trans>
              Instapaper is temporarily offline for maintenance. Nothing very "insta"
              about that, but we'll be back soon.
            </Trans>
          </p>
        </>
      );
    case 'rateLimit':
      return (
        <>
          <h1 className="text-copy-primary text-2xl font-semibold">
            <Trans>Whoa, gonna have to cut you off right there.</Trans>
          </h1>
          <p className="text-copy-secondary max-w-sm text-sm text-balance">
            <Trans>
              You've had one too many requests. Give it a moment and try again.
            </Trans>
          </p>
        </>
      );
    case 'server':
    default:
      return (
        <>
          <h1 className="text-copy-primary text-2xl font-semibold">
            <Trans>Something went wrong.</Trans>
          </h1>
          <p className="text-copy-secondary max-w-sm text-sm text-balance">
            <Trans>
              Don't panic — usually these errors aren't that serious. Try again, or head
              back home.
            </Trans>
          </p>
        </>
      );
  }
}

function ErrorPage({ error, reset }: ErrorComponentProps) {
  const variant = getErrorVariant(error);

  return (
    <div className="bg-surface-primary flex min-h-screen flex-col items-center justify-start gap-3 px-6 pt-[20vh] text-center">
      <ThemeLogo className="mb-2 h-9" />
      <ErrorCopy variant={variant} />
      <div className="mt-2 flex items-center gap-3">
        <button type="button" className="button-primary" onClick={reset}>
          <Trans>Try again</Trans>
        </button>
        <Link to="/home" className="button-secondary">
          <Trans>Back home</Trans>
        </Link>
      </div>
    </div>
  );
}

export default ErrorPage;
